'use client';

import * as React from 'react';
import { Drawer as DrawerPrimitive } from '@base-ui/react/drawer';
import { VisuallyHidden } from 'radix-ui';

import { cn, useIosKeyboardHeight } from '@/lib/core';

/**
 * `onOpenChange` for drawers that must never close from a dismissal *gesture* (escape key,
 * outside press, swipe) — only from an explicit call-site action (e.g. a back button).
 * Replaces the old `onEscapeKeyDown`/`onPointerDownOutside` `.preventDefault()` pair, which
 * lived on `SheetContent`; the Base UI equivalent lives on `Drawer` (Root), not `DrawerContent`.
 */
function blockDismiss(_open: boolean, eventDetails: { cancel: () => void }) {
  eventDetails.cancel();
}

type DrawerContextProps = {
  hasSnapPoints: boolean;
  modal: DrawerPrimitive.Root.Props['modal'];
  showSwipeHandle: boolean;
  swipeDirection: NonNullable<DrawerPrimitive.Root.Props['swipeDirection']>;
};

const DrawerContext = React.createContext<DrawerContextProps | null>(null);

function useDrawer() {
  const context = React.useContext(DrawerContext);
  if (!context) throw new Error('useDrawer must be used within a Drawer.');
  return context;
}

function Drawer({
  modal = true,
  showSwipeHandle = false,
  snapPoints,
  swipeDirection = 'down',
  ...props
}: DrawerPrimitive.Root.Props & { showSwipeHandle?: boolean }) {
  const hasSnapPoints = snapPoints != null && snapPoints.length > 0;
  const contextValue = React.useMemo(
    () => ({ hasSnapPoints, modal, showSwipeHandle, swipeDirection }),
    [hasSnapPoints, modal, showSwipeHandle, swipeDirection]
  );

  return (
    <DrawerContext.Provider value={contextValue}>
      <DrawerPrimitive.Root
        data-slot="drawer"
        modal={modal}
        snapPoints={snapPoints}
        swipeDirection={swipeDirection}
        {...props}
      />
    </DrawerContext.Provider>
  );
}

function DrawerTrigger({ ...props }: DrawerPrimitive.Trigger.Props) {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

function DrawerPortal({ ...props }: DrawerPrimitive.Portal.Props) {
  return <DrawerPrimitive.Portal data-slot="drawer-portal" {...props} />;
}

function DrawerClose({ ...props }: DrawerPrimitive.Close.Props) {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

/**
 * A region that opens the drawer when swiped, for drawers that keep a strip of themselves on
 * screen while closed (a peeking bottom bar over a map, say).
 *
 * Must be rendered inside `Drawer`, not beside it. It has no styles of its own — give it the
 * bounds of the region you want to be grabbable. `swipeDirection` defaults to the opposite of the
 * root's, so a `'down'`-to-dismiss drawer opens on an upward swipe without configuration.
 */
function DrawerSwipeArea({ ...props }: DrawerPrimitive.SwipeArea.Props) {
  return <DrawerPrimitive.SwipeArea data-slot="drawer-swipe-area" {...props} />;
}

function DrawerOverlay({ className, children, ...props }: DrawerPrimitive.Backdrop.Props) {
  return (
    <DrawerPrimitive.Backdrop
      data-slot="drawer-overlay"
      className={cn(
        'bg-jncquoi-neutral-1000/40 fixed inset-0 z-80 min-h-dvh',
        'opacity-[max(var(--drawer-overlay-min-opacity,0),calc(1-var(--drawer-swipe-progress)))]',
        'transition-opacity duration-300 ease-out select-none',
        'data-ending-style:pointer-events-none data-ending-style:opacity-0 data-ending-style:duration-300 data-starting-style:opacity-0 data-swiping:duration-0',
        'supports-[-webkit-touch-callout:none]:absolute',
        className
      )}
      {...props}
    >
      {children}
    </DrawerPrimitive.Backdrop>
  );
}

function DrawerSwipeHandle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-swipe-handle"
      aria-hidden="true"
      className={cn(
        'bg-jncquoi-neutral-300 relative z-10 mx-auto mt-4 h-1 w-10 shrink-0 cursor-grab rounded-full transition-opacity duration-200 active:cursor-grabbing',
        className
      )}
      {...props}
    />
  );
}

function DrawerContent({
  className,
  overlayClassName,
  overlayContent,
  disableSwipe = false,
  children,
  ...props
}: DrawerPrimitive.Popup.Props & {
  /** Override the dimming overlay, e.g. `"bg-transparent"` when content sits over a live backdrop (a map, etc). */
  overlayClassName?: string;
  /**
   * Rendered as children of the overlay, inside the same portal/stacking layer as the
   * drawer content itself — use for persistent page chrome (a map, a header) that needs
   * to share stacking context with the drawer rather than living outside the portal,
   * where it can be fought over by third-party widgets with their own internal z-index (e.g. Google Maps).
   */
  overlayContent?: React.ReactNode;
  /**
   * Suppresses the swipe-to-dismiss gesture across the entire popup — for persistent panels
   * dismissed only via an explicit in-content action (pair with `blockDismiss` on the `Drawer` root).
   */
  disableSwipe?: boolean;
}) {
  const { hasSnapPoints, modal, showSwipeHandle, swipeDirection } = useDrawer();
  const swipeAxis = swipeDirection === 'down' || swipeDirection === 'up' ? 'y' : 'x';
  const keyboardHeight = useIosKeyboardHeight();

  return (
    <DrawerPortal data-slot="drawer-portal">
      {modal === true && (
        <DrawerOverlay className={overlayClassName} data-snap-points={hasSnapPoints ? '' : undefined}>
          {overlayContent}
        </DrawerOverlay>
      )}
      <DrawerPrimitive.Viewport
        data-slot="drawer-viewport"
        data-modal={modal}
        className="pointer-events-none fixed inset-0 z-80 select-none data-[modal=true]:pointer-events-auto"
      >
        <DrawerPrimitive.Popup
          data-slot="drawer-popup"
          data-swipe-axis={swipeAxis}
          data-snap-points={hasSnapPoints ? '' : undefined}
          className={cn(
            'bg-jncquoi-neutral-00 group/drawer-popup pointer-events-auto fixed z-80 m-(--drawer-inset,0px) flex h-(--drawer-content-height) max-h-(--drawer-content-max-height,none) min-h-0 w-(--drawer-content-width,auto) transform-[translate3d(var(--translate-x,0px),var(--translate-y,0px),0)] flex-col transition-[transform,height,opacity] duration-420 ease-[cubic-bezier(0.32,0.72,0,1)] will-change-transform outline-none select-none [interpolate-size:allow-keywords]',
            'after:bg-jncquoi-neutral-00 after:pointer-events-none after:absolute data-[swipe-axis=x]:after:inset-y-0 data-[swipe-axis=x]:after:w-(--bleed) data-[swipe-axis=y]:after:inset-x-0 data-[swipe-axis=y]:after:h-(--bleed) data-[swipe-direction=down]:after:top-full data-[swipe-direction=left]:after:right-full data-[swipe-direction=right]:after:left-full data-[swipe-direction=up]:after:bottom-full',
            '[--bleed:3rem] [--drawer-content-height:var(--drawer-height,auto)] data-[swipe-axis=x]:[--drawer-content-width:75%] data-[swipe-axis=y]:[--drawer-content-max-height:calc(100dvh-6rem)] data-[swipe-axis=y]:data-snap-points:[--drawer-content-height:100dvh] data-[swipe-axis=x]:sm:[--drawer-content-width:24rem]',
            'data-ending-style:opacity-[0.9999] data-ending-style:duration-300 data-swiping:duration-0',
            'data-[swipe-axis=y]:inset-x-0',
            'data-[swipe-axis=x]:inset-y-0 data-[swipe-axis=x]:flex-row',
            'data-[swipe-direction=down]:bottom-0 data-[swipe-direction=down]:origin-bottom data-[swipe-direction=down]:rounded-t-sm data-[swipe-direction=down]:[--closed-transform:translate3d(0,calc(100%+2px),0)] data-[swipe-direction=down]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y))]',
            'data-[swipe-direction=up]:top-0 data-[swipe-direction=up]:origin-top data-[swipe-direction=up]:rounded-b-sm data-[swipe-direction=up]:[--closed-transform:translate3d(0,calc(-100%-2px),0)] data-[swipe-direction=up]:[--translate-y:calc(var(--drawer-snap-point-offset,0px)+var(--drawer-swipe-movement-y))]',
            'data-[swipe-direction=left]:left-0 data-[swipe-direction=left]:origin-left data-[swipe-direction=left]:rounded-r-sm data-[swipe-direction=left]:[--closed-transform:translate3d(calc(-100%-2px),0,0)] data-[swipe-direction=left]:[--translate-x:var(--drawer-swipe-movement-x)]',
            'data-[swipe-direction=right]:right-0 data-[swipe-direction=right]:origin-right data-[swipe-direction=right]:rounded-l-sm data-[swipe-direction=right]:[--closed-transform:translate3d(calc(100%+2px),0,0)] data-[swipe-direction=right]:[--translate-x:var(--drawer-swipe-movement-x)]',
            'data-ending-style:transform-(--closed-transform) data-starting-style:transform-(--closed-transform)',
            className
          )}
          {...props}
        >
          {showSwipeHandle && <DrawerSwipeHandle />}
          <DrawerPrimitive.Content
            data-slot="drawer-content"
            className="flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain rounded-[inherit] transition-opacity duration-300 ease-out select-text"
          >
            <VisuallyHidden.Root>
              <DrawerPrimitive.Title />
              <DrawerPrimitive.Description />
            </VisuallyHidden.Root>
            {disableSwipe ? (
              <div data-base-ui-swipe-ignore className="contents">
                {children}
              </div>
            ) : (
              children
            )}
            {/* Reserves space above the iOS keyboard, additive to whatever bottom padding the
                drawer's own content already has, so the keyboard never overlays a focused input. */}
            <div
              aria-hidden
              className="shrink-0 transition-[height] duration-300 ease-out"
              style={{ height: keyboardHeight }}
            />
          </DrawerPrimitive.Content>
        </DrawerPrimitive.Popup>
      </DrawerPrimitive.Viewport>
    </DrawerPortal>
  );
}

function DrawerHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return <div data-slot="drawer-header" className={cn('flex flex-col gap-4 px-4', className)} {...props} />;
}

function DrawerFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="drawer-footer"
      className={cn(
        'border-jncquoi-neutral-400 flex flex-col gap-2 border-t-[0.5px] px-4 pt-6 pb-10.5',
        className
      )}
      {...props}
    />
  );
}

function DrawerTitle({ className, ...props }: DrawerPrimitive.Title.Props) {
  return (
    <DrawerPrimitive.Title
      data-slot="drawer-title"
      className={cn(
        'font-pp-neue-montreal text-jncquoi-neutral-1000 text-2xl leading-none font-semibold',
        className
      )}
      {...props}
    />
  );
}

function DrawerDescription({ className, ...props }: DrawerPrimitive.Description.Props) {
  return (
    <DrawerPrimitive.Description
      data-slot="drawer-description"
      className={cn(
        'font-pp-neue-montreal text-jncquoi-neutral-1000 text-base leading-none font-[350] tracking-[0.25px]',
        className
      )}
      {...props}
    />
  );
}

export {
  Drawer,
  DrawerTrigger,
  DrawerClose,
  DrawerPortal,
  DrawerOverlay,
  DrawerSwipeArea,
  DrawerSwipeHandle,
  DrawerContent,
  DrawerHeader,
  DrawerFooter,
  DrawerTitle,
  DrawerDescription,
  blockDismiss,
};
